'use client';

import { useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  PolarAngleAxis,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge, Button, Card, DataTable, ErrorState, Inline, Skeleton, Stack, Typography, type Column } from '@org/design-system';
import { WidgetRegistry } from '@org/runtime-engine';
import type {
  ShadcnDashboardBarChartWidget,
  ShadcnDashboardKpiWidget,
  ShadcnDashboardLineChartWidget,
  ShadcnDashboardRecentListWidget,
  ShadcnDashboardStatRingWidget,
  ShadcnDashboardPluginWidget,
  ShadcnDashboardWidget,
} from '../types';
import { getRegisteredWidget } from '../plugin-runtime';

interface AnalyticsGridRendererProps {
  apiBaseUrl: string;
  widgets: ShadcnDashboardWidget[];
}

function resolveWidgetEndpoint(apiBaseUrl: string, entity: string, aggregation: string): string {
  const base = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
  const normalizedEntity = entity.startsWith('/') ? entity.slice(1) : entity;
  return `${base}/${normalizedEntity}/stats/${aggregation}`;
}

function buildWidgetQueryKey(widget: ShadcnDashboardWidget): string[] {
  return ['analytics-widget', widget.type, widget.entity, widget.aggregation, widget.id ?? 'default'];
}

async function fetchWidget(apiBaseUrl: string, widget: ShadcnDashboardWidget): Promise<unknown> {
  const response = await fetch(resolveWidgetEndpoint(apiBaseUrl, widget.entity, widget.aggregation));

  if (!response.ok) {
    throw new Error(`Failed to load analytics widget ${widget.type}`);
  }

  return response.json() as Promise<unknown>;
}

function WidgetShell({
  widget,
  children,
}: {
  widget: ShadcnDashboardWidget;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => buildWidgetQueryKey(widget), [widget]);
  const label = 'label' in widget && widget.label ? widget.label : widget.type;

  return (
    <Card className="h-full p-4">
      <Stack gap={3}>
        <Inline gap={2} align="center" justify="between">
          <Typography variant="small">{label}</Typography>
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Refresh ${label}`}
            onClick={() => {
              void queryClient.invalidateQueries({ queryKey });
            }}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </Inline>
        {children}
      </Stack>
    </Card>
  );
}

function WidgetLoading({ widget }: { widget: ShadcnDashboardWidget }) {
  return (
    <WidgetShell widget={widget}>
      <Stack gap={3}>
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-40 w-full" />
      </Stack>
    </WidgetShell>
  );
}

function KpiWidget({ apiBaseUrl, widget }: { apiBaseUrl: string; widget: ShadcnDashboardKpiWidget }) {
  const query = useQuery({
    queryKey: buildWidgetQueryKey(widget),
    queryFn: () => fetchWidget(apiBaseUrl, widget),
  });

  if (query.isLoading) return <WidgetLoading widget={widget} />;

  if (query.isError) {
    return (
      <WidgetShell widget={widget}>
        <ErrorState title="Unable to load widget" />
      </WidgetShell>
    );
  }

  const data = query.data && typeof query.data === 'object' ? (query.data as Record<string, unknown>) : {};
  const value = data[widget.valueKey];
  const trend = Number(data[widget.trendKey] ?? 0);

  return (
    <WidgetShell widget={widget}>
      <Stack gap={2}>
        <Typography variant="h1" className="font-display">{String(value ?? '—')}</Typography>
        <Badge variant={trend >= 0 ? 'default' : 'destructive'}>{trend >= 0 ? `+${trend}%` : `${trend}%`}</Badge>
      </Stack>
    </WidgetShell>
  );
}

function BarChartWidget({ apiBaseUrl, widget }: { apiBaseUrl: string; widget: ShadcnDashboardBarChartWidget }) {
  const query = useQuery({ queryKey: buildWidgetQueryKey(widget), queryFn: () => fetchWidget(apiBaseUrl, widget) });

  if (query.isLoading) return <WidgetLoading widget={widget} />;
  if (query.isError) {
    return (
      <WidgetShell widget={widget}>
        <ErrorState title="Unable to load widget" />
      </WidgetShell>
    );
  }

  const rows = Array.isArray(query.data) ? (query.data as Record<string, unknown>[]) : [];

  return (
    <WidgetShell widget={widget}>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={widget.xKey} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey={widget.yKey} fill="var(--primary)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </WidgetShell>
  );
}

function LineChartWidget({ apiBaseUrl, widget }: { apiBaseUrl: string; widget: ShadcnDashboardLineChartWidget }) {
  const query = useQuery({ queryKey: buildWidgetQueryKey(widget), queryFn: () => fetchWidget(apiBaseUrl, widget) });

  if (query.isLoading) return <WidgetLoading widget={widget} />;
  if (query.isError) {
    return (
      <WidgetShell widget={widget}>
        <ErrorState title="Unable to load widget" />
      </WidgetShell>
    );
  }

  const rows = Array.isArray(query.data) ? (query.data as Record<string, unknown>[]) : [];

  return (
    <WidgetShell widget={widget}>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={widget.xKey} />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey={widget.yKey} stroke="var(--primary)" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </WidgetShell>
  );
}

function RecentListWidget({ apiBaseUrl, widget }: { apiBaseUrl: string; widget: ShadcnDashboardRecentListWidget }) {
  const query = useQuery({ queryKey: buildWidgetQueryKey(widget), queryFn: () => fetchWidget(apiBaseUrl, widget) });

  if (query.isLoading) return <WidgetLoading widget={widget} />;
  if (query.isError) {
    return (
      <WidgetShell widget={widget}>
        <ErrorState title="Unable to load widget" />
      </WidgetShell>
    );
  }

  const rows = Array.isArray(query.data) ? (query.data as Record<string, unknown>[]) : [];
  const columns: Column<Record<string, unknown>>[] = widget.columns.map((col) => ({
    key: col,
    header: col,
    render: (row) => <Typography variant="small">{String(row[col] ?? '—')}</Typography>,
  }));

  return (
    <WidgetShell widget={widget}>
      <DataTable data={rows.slice(0, 5)} columns={columns} keyExtractor={(row, i) => String(i)} emptyMessage="No items found." />
    </WidgetShell>
  );
}

function StatRingWidget({ apiBaseUrl, widget }: { apiBaseUrl: string; widget: ShadcnDashboardStatRingWidget }) {
  const query = useQuery({ queryKey: buildWidgetQueryKey(widget), queryFn: () => fetchWidget(apiBaseUrl, widget) });

  if (query.isLoading) return <WidgetLoading widget={widget} />;
  if (query.isError) {
    return (
      <WidgetShell widget={widget}>
        <ErrorState title="Unable to load widget" />
      </WidgetShell>
    );
  }

  const data = query.data && typeof query.data === 'object' ? (query.data as Record<string, unknown>) : {};
  const value = Number(data[widget.valueKey] ?? 0);
  const total = Number(data[widget.totalKey] ?? 0);
  const percent = total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <WidgetShell widget={widget}>
      <Stack gap={2} align="center">
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart
              innerRadius="60%"
              outerRadius="100%"
              data={[{ name: widget.label ?? 'value', value: percent, fill: 'var(--primary)' }]}
              startAngle={90}
              endAngle={-270}
            >
              <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
              <RadialBar dataKey="value" background cornerRadius={10} />
              <Tooltip />
            </RadialBarChart>
          </ResponsiveContainer>
        </div>
        <Typography color="muted" variant="small">{percent}%</Typography>
      </Stack>
    </WidgetShell>
  );
}

function PluginWidget({ apiBaseUrl, widget }: { apiBaseUrl: string; widget: ShadcnDashboardPluginWidget }) {
  const PluginRenderer = getRegisteredWidget(widget.renderer);

  if (!PluginRenderer) {
    return (
      <WidgetShell widget={widget}>
        <ErrorState title={`Plugin renderer not found: ${widget.renderer}`} />
      </WidgetShell>
    );
  }

  return (
    <WidgetShell widget={widget}>
      <PluginRenderer widget={widget} apiBaseUrl={apiBaseUrl} />
    </WidgetShell>
  );
}

function renderWidget(apiBaseUrl: string, widget: ShadcnDashboardWidget) {
  const pluginRenderer = WidgetRegistry.resolve(widget.type);
  if (pluginRenderer) {
    const RendererComponent = pluginRenderer.component;
    return <RendererComponent apiBaseUrl={apiBaseUrl} widgetConfig={widget as Record<string, unknown>} />;
  }

  if (widget.type === 'kpi') return <KpiWidget apiBaseUrl={apiBaseUrl} widget={widget} />;
  if (widget.type === 'bar-chart') return <BarChartWidget apiBaseUrl={apiBaseUrl} widget={widget} />;
  if (widget.type === 'line-chart') return <LineChartWidget apiBaseUrl={apiBaseUrl} widget={widget} />;
  if (widget.type === 'recent-list') return <RecentListWidget apiBaseUrl={apiBaseUrl} widget={widget} />;
  if (widget.type === 'stat-ring') return <StatRingWidget apiBaseUrl={apiBaseUrl} widget={widget} />;
  if (widget.type === 'plugin') return <PluginWidget apiBaseUrl={apiBaseUrl} widget={widget as ShadcnDashboardPluginWidget} />;

  return (
    <WidgetShell widget={widget}>
      <ErrorState title={`Unsupported widget type: ${widget.type}`} />
    </WidgetShell>
  );
}

export function AnalyticsGridRenderer({ apiBaseUrl, widgets }: AnalyticsGridRendererProps) {
  return (
    <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(12, 1fr)' }}>
      {widgets.map((widget, index) => {
        const span = Math.min(12, Math.max(1, widget.span ?? 4));
        const rowSpan = Math.min(12, Math.max(1, widget.rowSpan ?? 1));

        return (
          <div
            key={`${widget.type}-${widget.id ?? index}`}
            style={{ gridColumn: `span ${span} / span ${span}`, gridRow: `span ${rowSpan} / span ${rowSpan}` }}
          >
            {renderWidget(apiBaseUrl, widget)}
          </div>
        );
      })}
    </div>
  );
}
